import { z } from 'zod';
import { addDecimal, compareDecimal } from '../utils/decimal.js';
import { currencyCodeSchema, idSchema, percentSchema } from './common.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { foreignAdvanceAttributionReviewSchema, foreignAdvanceAttributionSelectionSchema, foreignAdvanceRefundSelectionSchema, } from './foreign-advance-attribution.js';
// These fields describe a choice for review; their presence never proves source facts.
const canonicalGross = z.string().regex(/^(?:0|[1-9]\d{0,13})(?:\.\d{1,4})?$/u);
const sourceHash = z.string().regex(/^[a-f0-9]{64}$/u);
export const advanceRequestOptionsQuerySchema = z
    .object({
    party: z.enum(['CUSTOMER', 'VENDOR']),
    partnerId: idSchema,
    asOfDate: ledgerDateSchema,
    currency: currencyCodeSchema.optional(),
    cursor: idSchema.optional(),
})
    .strict();
export const advanceRequestOptionSchema = z
    .object({
    id: idSchema,
    sourceDocumentId: idSchema,
    sourceVersion: z.number().int().positive(),
    number: z.string().min(1).max(200),
    currency: currencyCodeSchema,
    recognitionDate: ledgerDateSchema,
    availableGross: canonicalGross,
    buckets: z
        .array(z
        .object({
        sourceBucketIndex: z.number().int().nonnegative(),
        rate: z.string(),
        sourceGross: canonicalGross,
        availableGross: canonicalGross,
    })
        .strict())
        .min(1)
        .max(100),
})
    .strict();
export const advanceRequestOptionsSchema = z
    .object({
    data: z.array(advanceRequestOptionSchema).max(100),
    nextCursor: idSchema.nullable(),
})
    .strict();
const refundAmount = z.string().regex(/^(?:0|[1-9]\d{0,13})\.\d{4}$/u);
const positiveRefundAmount = refundAmount.refine((value) => refundAmount.safeParse(value).success && compareDecimal(value, '0') > 0, 'ledger.errors.sourceInvalid');
export const advanceRefundSourcesQuerySchema = z
    .object({
    party: z.enum(['CUSTOMER', 'VENDOR']),
    paymentId: idSchema,
    asOfDate: ledgerDateSchema,
    cursor: idSchema.optional(),
})
    .strict();
const refundSourceBucketSchema = z
    .object({
    sourceBucketIndex: z.number().int().nonnegative().max(10000),
    rate: percentSchema,
    availableGross: positiveRefundAmount,
    sourceGross: positiveRefundAmount,
    sourceLegalNet: refundAmount,
    sourceLegalTax: refundAmount,
    sourceDeductibleTax: refundAmount,
})
    .strict()
    .superRefine((bucket, context) => {
    if (![
        bucket.availableGross,
        bucket.sourceGross,
        bucket.sourceLegalNet,
        bucket.sourceLegalTax,
        bucket.sourceDeductibleTax,
    ].every((value) => refundAmount.safeParse(value).success))
        return;
    if (compareDecimal(bucket.availableGross, bucket.sourceGross) > 0 ||
        addDecimal(bucket.sourceLegalNet, bucket.sourceLegalTax, 4) !== bucket.sourceGross ||
        compareDecimal(bucket.sourceDeductibleTax, bucket.sourceLegalTax) > 0)
        context.addIssue({ code: 'custom', message: 'ledger.errors.sourceInvalid' });
});
export const advanceRefundSourceSchema = z
    .object({
    sourceInvoiceId: idSchema,
    sourceVersion: z.number().int().positive(),
    party: z.enum(['CUSTOMER', 'VENDOR']),
    currency: z.string().regex(/^[A-Z]{3}$/u),
    paymentCurrency: z
        .string()
        .regex(/^[A-Z]{3}$/u)
        .optional(),
    sourceNumber: z.string().min(1).max(200),
    allocationId: idSchema,
    paymentId: idSchema,
    paymentVersion: z.number().int().positive(),
    paymentDate: ledgerDateSchema,
    availableGross: positiveRefundAmount,
    buckets: z.array(refundSourceBucketSchema).min(1).max(100),
})
    .strict()
    .superRefine((source, context) => {
    if ((source.currency !== 'EUR' && !source.paymentCurrency) ||
        (source.paymentCurrency && ![source.currency, 'EUR'].includes(source.paymentCurrency)))
        context.addIssue({ code: 'custom', message: 'ledger.errors.sourceInvalid' });
    if (!refundAmount.safeParse(source.availableGross).success ||
        !source.buckets.every((bucket) => refundAmount.safeParse(bucket.availableGross).success))
        return;
    if (new Set(source.buckets.map((bucket) => bucket.sourceBucketIndex)).size !==
        source.buckets.length ||
        source.buckets.reduce((sum, bucket) => addDecimal(sum, bucket.availableGross, 4), '0.0000') !== source.availableGross)
        context.addIssue({ code: 'custom', message: 'ledger.errors.sourceInvalid' });
});
export const advanceRefundSourcesSchema = z
    .object({ data: z.array(advanceRefundSourceSchema).max(200), nextCursor: idSchema.nullable() })
    .strict()
    .superRefine((page, context) => {
    if (new Set(page.data.map((source) => source.allocationId)).size !== page.data.length)
        context.addIssue({ code: 'custom', message: 'ledger.errors.sourceInvalid' });
});
export const advanceRefundCorrectionSchema = z
    .object({
    party: z.enum(['CUSTOMER', 'VENDOR']),
    refundPaymentVersion: z.number().int().positive(),
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(1000),
    idempotencyKey: z.string().min(8).max(100),
})
    .strict();
export const advanceGrossChoiceSchema = z
    .object({ sourceBucketIndex: z.number().int().nonnegative().max(10000), gross: canonicalGross })
    .strict();
export const advanceAttributionEvidenceRefSchema = z
    .object({
    kind: z.enum(['SOURCE_DOCUMENT', 'PAYMENT', 'ATTACHMENT']),
    id: idSchema,
    version: z.string().min(1).max(100),
    contentHash: sourceHash,
})
    .strict();
export const advanceAttributionChoiceSchema = z
    .object({
    method: z.enum(['SINGLE_BUCKET', 'EXPLICIT_SOURCE', 'REVIEWED_PRORATA']),
    buckets: z.array(advanceGrossChoiceSchema).min(1).max(100),
    // The server independently resolves required source/payment evidence; client
    // references are additional claims and are never accepted as proof alone.
    evidenceRefs: z.array(advanceAttributionEvidenceRefSchema).max(100).default([]),
    reason: z.string().trim().min(10).max(1000),
})
    .strict()
    .superRefine((value, context) => {
    const indices = new Set();
    for (const [index, bucket] of value.buckets.entries()) {
        if (indices.has(bucket.sourceBucketIndex))
            context.addIssue({
                code: 'custom',
                path: ['buckets', index],
                message: 'Source bucket is duplicated',
            });
        indices.add(bucket.sourceBucketIndex);
    }
    if (!value.buckets.some((bucket) => canonicalGross.safeParse(bucket.gross).success && compareDecimal(bucket.gross, '0') > 0))
        context.addIssue({
            code: 'custom',
            path: ['buckets'],
            message: 'A positive attributed amount is required',
        });
    if (value.method === 'SINGLE_BUCKET' &&
        value.buckets.filter((bucket) => canonicalGross.safeParse(bucket.gross).success && compareDecimal(bucket.gross, '0') > 0).length !== 1)
        context.addIssue({
            code: 'custom',
            path: ['buckets'],
            message: 'A single source bucket is required',
        });
});
export const advanceAttributionConfirmationSchema = z
    .object({ choice: advanceAttributionChoiceSchema, previewHash: sourceHash })
    .strict();
export const advanceReceiptAttributionConfirmationSchema = advanceAttributionConfirmationSchema.extend({
    foreign: foreignAdvanceAttributionSelectionSchema.optional(),
});
export const advanceRefundAttributionConfirmationSchema = advanceAttributionConfirmationSchema.extend({
    foreign: foreignAdvanceRefundSelectionSchema.optional(),
});
const previewBase = {
    party: z.enum(['CUSTOMER', 'VENDOR']),
    paymentId: idSchema,
    paymentVersion: z.number().int().positive(),
    sourceVersion: z.number().int().positive(),
    effectiveDate: ledgerDateSchema,
    amount: canonicalGross.refine((value) => canonicalGross.safeParse(value).success && compareDecimal(value, '0') > 0),
    choice: advanceAttributionChoiceSchema,
};
function validatePreviewAmount(input, context) {
    if (!canonicalGross.safeParse(input.amount).success ||
        input.choice.buckets.some((bucket) => !canonicalGross.safeParse(bucket.gross).success))
        return;
    const total = input.choice.buckets.reduce((sum, bucket) => addDecimal(sum, bucket.gross, 4), '0');
    if (compareDecimal(total, input.amount) !== 0)
        context.addIssue({
            code: 'custom',
            path: ['choice', 'buckets'],
            message: 'Attribution must equal the payment amount',
        });
}
export const advanceAttributionReceiptPreviewSchema = z
    .object({ ...previewBase, foreign: foreignAdvanceAttributionSelectionSchema.optional() })
    .strict()
    .superRefine(validatePreviewAmount);
export const advanceReceiptReassignmentPreviewSchema = z
    .object({
    targetInvoiceId: idSchema,
    receipt: advanceAttributionReceiptPreviewSchema,
})
    .strict();
export const advanceReceiptReassignmentSchema = advanceReceiptReassignmentPreviewSchema
    .extend({
    previewHash: sourceHash,
    idempotencyKey: z.string().trim().min(8).max(100),
})
    .strict();
export const advanceAttributionRefundPreviewSchema = z
    .object({
    ...previewBase,
    allocationId: idSchema,
    refundPaymentId: idSchema,
    refundPaymentVersion: z.number().int().positive(),
    foreign: foreignAdvanceRefundSelectionSchema.optional(),
})
    .strict()
    .superRefine(validatePreviewAmount);
const reviewBucket = z
    .object({
    sourceBucketIndex: z.number().int().nonnegative().max(10000),
    rate: z.string().regex(/^\d{1,3}(?:\.\d{1,4})?$/u),
    sourceGross: canonicalGross,
    sourceLegalNet: canonicalGross,
    sourceLegalTax: canonicalGross,
    sourceDeductibleTax: canonicalGross,
    availableGross: canonicalGross,
    gross: canonicalGross,
    legalNet: canonicalGross,
    legalTax: canonicalGross,
    deductibleTax: canonicalGross,
    carryingAmount: canonicalGross,
})
    .strict();
export const advanceAttributionReviewSchema = z
    .object({
    schemaVersion: z.literal(1),
    kind: z.enum(['RECEIPT', 'REFUND']),
    previewHash: sourceHash,
    currency: z.literal('EUR'),
    sourceNumber: z.string().min(1).max(200),
    sourceVersion: z.number().int().positive(),
    method: z.enum(['SINGLE_BUCKET', 'EXPLICIT_SOURCE', 'REVIEWED_PRORATA']),
    reason: z.string().min(10).max(1000),
    paymentDate: ledgerDateSchema,
    refundDate: ledgerDateSchema.optional(),
    effectiveDate: ledgerDateSchema,
    gross: canonicalGross,
    buckets: z.array(reviewBucket).min(1).max(100),
})
    .strict()
    .superRefine((review, context) => {
    if ((review.kind === 'REFUND') !== Boolean(review.refundDate))
        context.addIssue({
            code: 'custom',
            path: ['refundDate'],
            message: 'Actual repayment date is required for refunds',
        });
    const values = [
        review.gross,
        ...review.buckets.flatMap((bucket) => [
            bucket.gross,
            bucket.sourceGross,
            bucket.sourceLegalNet,
            bucket.sourceLegalTax,
            bucket.sourceDeductibleTax,
            bucket.availableGross,
            bucket.legalNet,
            bucket.legalTax,
            bucket.deductibleTax,
            bucket.carryingAmount,
        ]),
    ];
    if (values.some((value) => !canonicalGross.safeParse(value).success))
        return;
    let gross = '0';
    const seen = new Set();
    for (const bucket of review.buckets) {
        if (seen.has(bucket.sourceBucketIndex) ||
            compareDecimal(addDecimal(bucket.sourceLegalNet, bucket.sourceLegalTax, 4), bucket.sourceGross) !== 0 ||
            compareDecimal(bucket.sourceDeductibleTax, bucket.sourceLegalTax) > 0 ||
            compareDecimal(bucket.availableGross, bucket.sourceGross) > 0 ||
            compareDecimal(bucket.gross, bucket.availableGross) > 0 ||
            compareDecimal(addDecimal(bucket.legalNet, bucket.legalTax, 4), bucket.gross) !== 0 ||
            compareDecimal(addDecimal(bucket.carryingAmount, bucket.deductibleTax, 4), bucket.gross) !==
                0 ||
            compareDecimal(bucket.deductibleTax, bucket.legalTax) > 0)
            context.addIssue({
                code: 'custom',
                path: ['buckets'],
                message: 'Inconsistent source bucket totals',
            });
        seen.add(bucket.sourceBucketIndex);
        gross = addDecimal(gross, bucket.gross, 4);
    }
    if (compareDecimal(gross, review.gross) !== 0 || compareDecimal(review.gross, '0') <= 0)
        context.addIssue({
            code: 'custom',
            path: ['gross'],
            message: 'Bucket gross does not equal reviewed total',
        });
});
export const advanceReceiptAttributionReviewSchema = z.union([
    advanceAttributionReviewSchema,
    foreignAdvanceAttributionReviewSchema,
]);
export const advanceRefundAttributionReviewSchema = advanceReceiptAttributionReviewSchema;
export const advanceRequestCancellationPreviewSchema = z
    .object({ version: z.number().int().positive(), effectiveDate: ledgerDateSchema })
    .strict();
export const advanceRequestCancellationSchema = advanceRequestCancellationPreviewSchema
    .extend({
    previewHash: sourceHash,
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: z.string().trim().min(8).max(100),
})
    .strict();
const cancellationPaymentReview = z
    .object({
    currency: currencyCodeSchema.optional(),
    sourceFundedNominal: canonicalGross.optional(),
    moneyDate: ledgerDateSchema,
    cashAmount: canonicalGross,
    fee: canonicalGross,
    gross: canonicalGross,
    sourceFunded: canonicalGross,
    allocatedElsewhere: canonicalGross,
    offsetTotal: canonicalGross,
    remainingCredit: canonicalGross,
    reversedOn: ledgerDateSchema.nullable(),
})
    .strict();
export const advanceRequestCancellationReviewSchema = z
    .object({
    schemaVersion: z.literal(1),
    party: z.enum(['CUSTOMER', 'VENDOR']),
    sourceNumber: z.string().min(1).max(200),
    sourceVersion: z.number().int().positive(),
    currency: currencyCodeSchema,
    effectiveDate: ledgerDateSchema,
    requestedGross: canonicalGross,
    sourcePaid: canonicalGross,
    activeApplications: canonicalGross,
    previewHash: sourceHash,
    payments: z.array(cancellationPaymentReview).max(100),
})
    .strict()
    .superRefine((review, context) => {
    const values = [
        review.requestedGross,
        review.sourcePaid,
        review.activeApplications,
        ...review.payments.flatMap((payment) => [
            ...(payment.sourceFundedNominal !== undefined ? [payment.sourceFundedNominal] : []),
            payment.cashAmount,
            payment.fee,
            payment.gross,
            payment.sourceFunded,
            payment.allocatedElsewhere,
            payment.offsetTotal,
            payment.remainingCredit,
        ]),
    ];
    if (values.some((value) => !canonicalGross.safeParse(value).success))
        return;
    let funded = '0';
    for (const payment of review.payments) {
        if ((review.currency !== 'EUR' &&
            (!payment.currency || payment.sourceFundedNominal === undefined)) ||
            (payment.currency && ![review.currency, 'EUR'].includes(payment.currency)))
            context.addIssue({
                code: 'custom',
                path: ['payments'],
                message: 'Explicit payment and invoice currencies are required',
            });
        funded = addDecimal(funded, payment.sourceFundedNominal ?? payment.sourceFunded, 4);
        if (review.party === 'CUSTOMER'
            ? compareDecimal(addDecimal(payment.cashAmount, payment.fee, 4), payment.gross) !== 0
            : compareDecimal(addDecimal(payment.gross, payment.fee, 4), payment.cashAmount) !== 0)
            context.addIssue({
                code: 'custom',
                path: ['payments'],
                message: 'Cash and fee do not reconcile',
            });
        const consumed = addDecimal(addDecimal(payment.sourceFunded, payment.allocatedElsewhere, 4), payment.offsetTotal, 4);
        if ((!payment.reversedOn &&
            compareDecimal(addDecimal(consumed, payment.remainingCredit, 4), payment.gross) !== 0) ||
            (payment.reversedOn && compareDecimal(payment.remainingCredit, '0') !== 0))
            context.addIssue({
                code: 'custom',
                path: ['payments'],
                message: 'Payment disposition does not reconcile',
            });
    }
    if (compareDecimal(funded, review.sourcePaid) !== 0 ||
        compareDecimal(review.activeApplications, '0') !== 0)
        context.addIssue({
            code: 'custom',
            path: ['sourcePaid'],
            message: 'Request balance does not reconcile',
        });
});
export const advanceRequestCancellationResultSchema = z
    .object({
    id: idSchema,
    status: z.literal('CANCELLED'),
    review: advanceRequestCancellationReviewSchema,
})
    .strict();
export const advanceRequestCancellationComparisonSchema = z
    .object({
    reviewedStateChanged: z.boolean(),
    reviewed: advanceRequestCancellationReviewSchema,
    current: advanceRequestCancellationReviewSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.reviewed.party !== value.current.party ||
        value.reviewed.currency !== value.current.currency ||
        value.reviewed.sourceNumber !== value.current.sourceNumber ||
        value.reviewed.effectiveDate !== value.current.effectiveDate ||
        value.reviewedStateChanged !== (value.reviewed.previewHash !== value.current.previewHash))
        context.addIssue({
            code: 'custom',
            message: 'Reviewed and current facts do not describe the same request date',
        });
});
export const advanceRequestSourceQuerySchema = z
    .object({ party: z.enum(['CUSTOMER', 'VENDOR']), sourceDocumentId: idSchema })
    .strict();
const cancellationSourceStateSchema = z
    .object({
    reviewed: advanceRequestCancellationReviewSchema,
    current: advanceRequestCancellationReviewSchema,
    reviewedStateChanged: z.boolean(),
})
    .strict();
export const advanceRequestSourceSchema = z
    .object({
    id: idSchema,
    sourceDocumentId: idSchema,
    sourceVersion: z.number().int().positive(),
    party: z.enum(['CUSTOMER', 'VENDOR']),
    currency: currencyCodeSchema,
    asOfDate: ledgerDateSchema,
    status: z.enum(['ACTIVE', 'CANCELLED']),
    cancellation: cancellationSourceStateSchema.nullable(),
})
    .strict()
    .superRefine((source, context) => {
    const cancellation = source.cancellation;
    if ((source.status === 'CANCELLED') !== Boolean(cancellation) ||
        (cancellation &&
            (cancellation.reviewed.party !== source.party ||
                cancellation.current.party !== source.party ||
                cancellation.reviewed.currency !== source.currency ||
                cancellation.current.currency !== source.currency ||
                cancellation.reviewed.sourceNumber !== cancellation.current.sourceNumber ||
                cancellation.current.effectiveDate < cancellation.reviewed.effectiveDate ||
                cancellation.current.effectiveDate !== source.asOfDate)))
        context.addIssue({
            code: 'custom',
            path: ['cancellation'],
            message: 'Inconsistent request cancellation context',
        });
});
export const advanceRequestSourceContextSchema = z
    .object({ data: advanceRequestSourceSchema.nullable() })
    .strict();
//# sourceMappingURL=advance-attribution.js.map